import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, ButtonGroup, ClickAwayListener, Grow, Paper, Popper, Stack } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

/**
 * Outlined button that runs its primary (first) action on click and exposes any
 * extra actions through a dropdown caret. With a single action it renders as a
 * plain button (no caret).
 *
 * @param {Array<{label: React.ReactNode, action: Function, disabled?: boolean, sx?: object}>} props.actions - first item is the primary action;
 *   an action's `sx` is forwarded to its button (e.g. to make a still-clickable action LOOK disabled)
 * @param {boolean} [props.disabled]
 * @param {string} [props.variant] - MUI Button variant (default 'outlined')
 * @param {string} [props.color] - MUI Button color (default 'primary')
 * @param {React.ElementType} [props.ButtonComponent] - component used to render the action buttons
 *   (primary and dropdown items). Defaults to MUI Button. It must accept `onClick`, `disabled` and
 *   children; used to inject behaviours such as `WorkerRequiredButton`. The dropdown caret always
 *   stays a plain Button.
 */
const SmartSplitButton = ({ actions = [], disabled = false, variant = 'outlined', color = 'primary', ButtonComponent = Button }) => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)

  if (actions.length === 0) return null

  const [primary, ...rest] = actions
  const hasMenu = rest.length > 0

  const handleToggle = () => setOpen((prev) => !prev)

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return
    setOpen(false)
  }

  const runAction = (action) => {
    setOpen(false)
    if (typeof action === 'function') action()
  }

  return (
    <>
      <ButtonGroup ref={anchorRef} variant={variant} color={color} disabled={disabled}>
        <ButtonComponent onClick={() => runAction(primary.action)} disabled={disabled || primary.disabled} sx={primary.sx}>
          {primary.label}
        </ButtonComponent>
        {hasMenu && (
          <Button
            size='small'
            onClick={handleToggle}
            aria-label='show more actions'
            sx={{ px: 0.5 }}
          >
            <ArrowDropDownIcon />
          </Button>
        )}
      </ButtonGroup>
      {hasMenu && (
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          transition
          placement='bottom-end'
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{ transformOrigin: placement.startsWith('bottom') ? 'right top' : 'right bottom' }}
            >
              <Paper sx={{ p: 0.5, width: anchorRef.current ? anchorRef.current.offsetWidth : 'auto' }}>
                <ClickAwayListener onClickAway={handleClose}>
                  <Stack spacing={0.5}>
                    {rest.map((item, index) => (
                      <ButtonComponent
                        key={index}
                        variant={variant}
                        color={color}
                        disabled={item.disabled}
                        sx={item.sx}
                        onClick={() => runAction(item.action)}
                        fullWidth
                      >
                        {item.label}
                      </ButtonComponent>
                    ))}
                  </Stack>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </>
  )
}

SmartSplitButton.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.node.isRequired,
    action: PropTypes.func,
    disabled: PropTypes.bool,
    sx: PropTypes.object
  })),
  disabled: PropTypes.bool,
  variant: PropTypes.string,
  color: PropTypes.string,
  ButtonComponent: PropTypes.elementType
}

export default SmartSplitButton
